import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { KeyRound, Download, FileText, Info, ChevronDown } from 'lucide-react';
import Message from '../components/Message';
import Loader from '../components/Loader';
import BillingForm from '../components/BillingForm';
import ProfileForm from '../components/ProfileForm';
import LicenseModal from '../components/LicenseModal';
import { Button, GlassCard, Badge } from '../components/ui';
import { downloadProduct } from '../utils/productDownloads';
import { motion } from 'framer-motion';
import { devLog, devError, sanitizeForLog } from '../utils/logger';
import { translateProduct } from '../utils/productTranslations';
import { formatDisplayPrice } from '../utils/currencyDisplay';

// Helper pour générer les URL d'API
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://api.alturionlabs.com' 
    : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

/** Uniquement : snapshot sur la ligne de commande, sinon règle du produit (API). Jamais déduit du prix. */
const resolveOrderItemRequiresLicense = (item) => {
  if (typeof item.requiresLicense === 'boolean') return item.requiresLicense;
  const p = item?.product;
  if (p && typeof p === 'object') return p.requiresLicense !== false;
  return true;
};

const tabBaseClass = 'px-3.5 py-2 text-[13px] font-medium transition-colors duration-200 rounded-btn';
const tabActiveClass = 'bg-white/[.06] text-body border border-white/12';
const tabInactiveClass = 'text-muted hover:text-body border border-transparent';

const ProfileScreen = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [purchasedProducts, setPurchasedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [downloadingInvoices, setDownloadingInvoices] = useState(new Set());
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseRequests, setLicenseRequests] = useState([]);
  
  // Récupérer l'onglet depuis l'URL (par exemple: ?tab=indicators)
  const params = new URLSearchParams(location.search);
  const tabFromUrl = params.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'profile');
  
  // Vérifier si l'utilisateur vient juste d'acheter (pour afficher un message)
  const isNewPurchase = params.get('newpurchase') === 'true';
  
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // Mettre à jour l'URL quand l'onglet change
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    navigate(`/profile?tab=${tabKey}`);
  };

  useEffect(() => {
    // Mettre à jour l'onglet actif si l'URL change
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  useEffect(() => {
    // Redirection si l'utilisateur n'est pas connecté
    if (!userInfo) {
      navigate('/login');
      return;
    }

    const fetchOrders = async () => {
      try {
        setLoading(true);
        const config = {
          headers: {
            Authorization: `Bearer ${userInfo.token}`,
          },
        };

        devLog('🔍 Tentative de récupération des commandes...');
        
        const { data } = await axios.get(
          getApiUrl('/api/orders/mine'),
          config
        );

        devLog('📦 Commandes reçues:', sanitizeForLog(data));
        
        // Vérifier que data est un tableau
        if (!Array.isArray(data)) {
          devError('❌ La réponse API n\'est pas un tableau:', sanitizeForLog(data));
          setOrders([]);
        } else {
          // Filtrer uniquement les commandes payées
          const paidOrders = data.filter(order => order.isPaid);
          devLog('💰 Commandes payées:', paidOrders.length);
          
          // S'assurer que chaque ordre a un _id et des orderItems valides
          const validatedOrders = paidOrders.map(order => {
            // S'assurer que orderItems est un tableau
            const orderItems = Array.isArray(order.orderItems) ? order.orderItems : [];
            devLog(`📝 Commande ${order._id}:`, {
              orderItems: orderItems.length,
              isPaid: order.isPaid,
              totalPrice: order.totalPrice
            });
            
            return {
              ...order,
              orderItems: orderItems
            };
          });
          
          setOrders(validatedOrders);
          
          // ✅ Extraire les produits achetés
          const purchasedProductsList = [];
          validatedOrders.forEach(order => {
            order.orderItems.forEach(item => {
              if (item.product && item.product.name) {
                purchasedProductsList.push({
                  productId: item.product._id,
                  productName: item.product.name,
                  price: item.price,
                  purchaseDate: order.createdAt,
                  orderId: order._id,
                  description: item.product.description,
                  requiresLicense: resolveOrderItemRequiresLicense(item),
                });
              }
            });
          });
          
          devLog('🛒 Produits achetés trouvés:', purchasedProductsList.length);
          purchasedProductsList.forEach(product => {
            devLog(`   - ${product.productName} (${product.price}€)`);
          });
          
          setPurchasedProducts(purchasedProductsList);

          // Récupérer les demandes de licence existantes de l'utilisateur
          try {
            const { data: myRequests } = await axios.get(getApiUrl('/api/license/my-requests'), config);
            setLicenseRequests(Array.isArray(myRequests) ? myRequests : []);
            devLog('🔑 Demandes de licence existantes:', myRequests?.length || 0);
          } catch (reqErr) {
            devError('❌ Erreur récupération demandes de licence:', sanitizeForLog(reqErr));
            setLicenseRequests([]);
          }
        }
        
        setLoading(false);
      } catch (error) {
        devError('❌ Erreur lors de la récupération des commandes:', sanitizeForLog(error));
        setError(
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message
        );
        setLoading(false);
      }
    };

    fetchOrders();
  }, [navigate, userInfo]);

  // Ajouter une fonction utilitaire pour vérifier si une date est valide
  const isValidDate = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  // Fonction helper pour télécharger une facture
  const downloadInvoice = async (orderId) => {
    try {
      setDownloadingInvoices(prev => new Set([...prev, orderId]));
      
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      };
      
      const response = await axios.get(getApiUrl(`/api/invoices/${orderId}`), config);
      
      if (response.data.invoicePdfUrl) {
        // Téléchargement direct sans ouvrir un nouvel onglet
        const link = document.createElement('a');
        link.href = response.data.invoicePdfUrl;
        link.download = `facture-${orderId.substring(0, 8)}.pdf`;
        
        // Ajouter temporairement au DOM et cliquer
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        setSuccessMessage(t('profile.orders.invoiceSuccess'));
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError("PDF non disponible");
      }
    } catch (err) {
      console.error('Erreur téléchargement facture:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      // Toujours retirer de l'état de téléchargement
      setDownloadingInvoices(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Fonctions pour gérer la modale de licence
  const handleLicenseRequest = (product) => {
    setSelectedProduct(product);
    setShowLicenseModal(true);
  };

  const handleLicenseSubmit = async (licenseData) => {
    try {
      setLicenseLoading(true);
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      await axios.post(
        getApiUrl('/api/license/request'),
        {
          productId: selectedProduct.productId,
          orderId: selectedProduct.orderId,
          machineIdName: licenseData.machineIdName,
        },
        config
      );

      setSuccessMessage('License request submitted successfully! You will receive a confirmation email.');
      setShowLicenseModal(false);
      setSelectedProduct(null);
      setTimeout(() => setSuccessMessage(null), 5000);
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLicenseLoading(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-page items-start px-6 pt-28 pb-24 md:pt-32">
      <div className="w-full mt-10">
      {isNewPurchase && (
          <div className="mb-6 bg-primary/10 border border-primary text-primary px-4 py-3 rounded-btn">
          <p className="font-medium">{t('profile.messages.thankYou')}</p>
          <p className="text-sm">{t('profile.messages.orderSuccess')}</p>
        </div>
      )}

      {/* Messages de succès et d'erreur */}
      {successMessage && (
        <div className="mb-6 bg-success/10 border border-success text-success px-4 py-3 rounded-btn">
          <p className="font-medium">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-danger/10 border border-danger text-danger px-4 py-3 rounded-btn">
          <p className="font-medium">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="text-danger hover:text-danger/80 text-sm mt-1"
          >
            {t('profile.messages.close')}
          </button>
        </div>
      )}

        <GlassCard hover={false} className="overflow-hidden">
          <div className="border-b border-white/[.08]">
          <nav className="flex flex-wrap gap-2 px-4 py-3" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('profile')}
              className={`${tabBaseClass} ${
                activeTab === 'profile' ? tabActiveClass : tabInactiveClass
              }`}
            >
              {t('profile.tabs.profile')}
            </button>
            <button
              onClick={() => handleTabChange('indicators')}
              className={`${tabBaseClass} ${
                activeTab === 'indicators' ? tabActiveClass : tabInactiveClass
              }`}
            >
              {t('profile.tabs.indicators')}
            </button>
            <button
              onClick={() => handleTabChange('orders')}
              className={`${tabBaseClass} ${
                activeTab === 'orders' ? tabActiveClass : tabInactiveClass
              }`}
            >
              {t('profile.tabs.orders')}
            </button>
          </nav>
        </div>

          <div className="p-6 min-h-[600px]">
          {loading ? (
            <Loader />
          ) : error ? (
            <Message variant="error">{error}</Message>
          ) : (
            <div>
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  {/* Informations du compte */}
                    <GlassCard hover={false}>
                      <div className="px-4 py-5 sm:px-6 border-b border-white/[.08]">
                        <h3 className="text-lg font-medium text-body">{t('profile.account.title')}</h3>
                    </div>
                    <div className="px-4 py-5 sm:p-6">
                      <dl className="space-y-4">
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-muted">{t('profile.account.name')}</dt>
                            <dd className="mt-1 text-sm text-body sm:mt-0 sm:col-span-2">{userInfo.name}</dd>
                        </div>
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-muted">{t('profile.account.email')}</dt>
                            <dd className="mt-1 text-sm text-body sm:mt-0 sm:col-span-2">{userInfo.email}</dd>
                        </div>
                      </dl>
                      <div className="mt-6">
                        <Button
                          variant="secondary"
                          onClick={() => setShowProfileForm(true)}
                        >
                          {t('profile.account.editButton')}
                        </Button>
                      </div>
                    </div>
                  </GlassCard>

                  {/* Informations de facturation */}
                    <GlassCard hover={false}>
                      <div className="px-4 py-5 sm:px-6 border-b border-white/[.08]">
                        <h3 className="text-lg font-medium text-body">{t('profile.billing.title')}</h3>
                        <p className="mt-1 text-sm text-muted">{t('profile.billing.description')}</p>
                    </div>
                    <div className="px-4 py-5 sm:p-6">
                      <dl className="space-y-4">
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-muted">{t('profile.billing.name')}</dt>
                            <dd className="mt-1 text-sm text-body sm:mt-0 sm:col-span-2">{userInfo.billingName || userInfo.name}</dd>
                        </div>
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-muted">{t('profile.billing.address')}</dt>
                            <dd className="mt-1 text-sm text-body sm:mt-0 sm:col-span-2">{userInfo.billingAddress || t('profile.billing.notProvidedFeminine')}</dd>
                        </div>
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-muted">{t('profile.billing.zipCode')}</dt>
                            <dd className="mt-1 text-sm text-body sm:mt-0 sm:col-span-2">{userInfo.billingZip || t('profile.billing.notProvided')}</dd>
                        </div>
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-muted">{t('profile.billing.city')}</dt>
                            <dd className="mt-1 text-sm text-body sm:mt-0 sm:col-span-2">{userInfo.billingCity || t('profile.billing.notProvidedFeminine')}</dd>
                        </div>
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-muted">{t('profile.billing.country')}</dt>
                            <dd className="mt-1 text-sm text-body sm:mt-0 sm:col-span-2">{userInfo.billingCountry || t('profile.billing.notProvided')}</dd>
                        </div>
                        <div className="sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-muted">{t('profile.billing.vat')}</dt>
                            <dd className="mt-1 text-sm text-body sm:mt-0 sm:col-span-2">{userInfo.billingVAT || t('profile.billing.notProvided')}</dd>
                        </div>
                      </dl>
                      <div className="mt-6">
                        <Button
                          variant="secondary"
                          onClick={() => setShowBillingForm(true)}
                        >
                          {t('profile.billing.updateButton')}
                        </Button>
                      </div>
                    </div>
                  </GlassCard>
                </div>
              )}

              {activeTab === 'indicators' && (
                <div className="space-y-6">
                  {/* Guide d'installation */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                  >
                    <GlassCard hover={false} className="p-4">
                      <details className="group">
                        <summary className="flex items-center text-sm text-muted hover:text-body cursor-pointer list-none">
                          <Info className="w-4 h-4 text-primary/70 mr-2 shrink-0" />
                          {t('profile.indicators.installGuide.title')}
                          <ChevronDown className="w-3.5 h-3.5 ml-2 group-open:rotate-180 transition-transform duration-300" />
                        </summary>
                        <div className="mt-4 pl-4 space-y-4">
                          <ol className="list-decimal list-inside space-y-2 text-sm text-muted">
                            {t('profile.indicators.installGuide.steps', { returnObjects: true }).map((step, index) => (
                              <li key={index}>{step}</li>
                            ))}
                          </ol>
                          <div className="text-xs text-muted/80 pt-2">
                            <p className="mb-1"><strong>{t('profile.indicators.installGuide.requirements')}</strong> {t('profile.indicators.installGuide.requirementsList')}</p>
                            <p>{t('profile.indicators.installGuide.support')} <a href="/support" className="text-primary/70 hover:text-primary">{t('profile.indicators.installGuide.contactSupport')}</a></p>
                          </div>
                        </div>
                      </details>
                    </GlassCard>
                  </motion.div>

                  {/* Guide d'activation de licence */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                  >
                    <GlassCard hover={false} className="p-4">
                      <details className="group">
                        <summary className="flex items-center text-sm text-muted hover:text-body cursor-pointer list-none">
                          <KeyRound className="w-4 h-4 text-primary/70 mr-2 shrink-0" />
                          {t('profile.indicators.licenseGuide.title')}
                          <ChevronDown className="w-3.5 h-3.5 ml-2 group-open:rotate-180 transition-transform duration-300" />
                        </summary>
                        <div className="mt-4 pl-4 space-y-4">
                          <ol className="list-decimal list-inside space-y-2 text-sm text-muted">
                            {t('profile.indicators.licenseGuide.steps', { returnObjects: true }).map((step, index) => (
                              <li key={index}>{step}</li>
                            ))}
                          </ol>
                          <div className="text-xs text-muted/80 pt-2">
                            <p className="mb-1"><strong>{t('profile.indicators.licenseGuide.requirements')}</strong> {t('profile.indicators.licenseGuide.requirementsList')}</p>
                            <p>{t('profile.indicators.licenseGuide.support')} <a href="/support" className="text-primary/70 hover:text-primary">{t('profile.indicators.licenseGuide.contactSupport')}</a></p>
                          </div>
                        </div>
                      </details>
                    </GlassCard>
                  </motion.div>

                  {/* Liste des indicateurs achetés */}
                  {Array.isArray(purchasedProducts) && purchasedProducts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {purchasedProducts.map((product) => {
                        // Créer un slug à partir du nom du produit pour les traductions
                        const createSlugFromName = (name) => {
                          const slugMap = {
                            'Volume Profile Premium': 'volume-profile-premium',
                            'Volume Profile Lite': 'volume-profile-lite',
                            'VWAP Premium': 'vwap-premium',
                            'VWAP Lite': 'vwap-lite',
                            'Trading Sessions': 'trading-sessions',
                            'Asian Session Levels': 'asian-session-levels',
                            'Enhanced Session Levels': 'enhanced-session-levels',
                            'Risk/Reward': 'risk-reward',
                            'Alturion Open Levels': 'open-levels',
                            'Alturion Open Range Breakout': 'orb',
                            'Bar Timer': 'bar-timer',
                            'NYPA': 'nypa',
                            'Economic Calendar': 'economic-calendar',
                            'Account Locker': 'account-locker',
                            'Footprint': 'footprint',
                            'Tape Power Meter': 'tape-power-meter',
                            'Fair Value Gap': 'fair-value-gap',
                            'Cumulative Delta': 'cumulative-delta',
                            'Delta Profile': 'delta-profile',
                            'Big Trades': 'big-trades'
                          };
                          return slugMap[name] || name.toLowerCase().replace(/\s+/g, '-');
                        };
                        
                        // Créer un objet produit avec slug pour la traduction
                        const productWithSlug = {
                          ...product,
                          name: product.productName,
                          slug: createSlugFromName(product.productName)
                        };
                        
                        // Traduire le produit selon la langue actuelle
                        console.log('🌍 Langue actuelle:', i18n.language);
                        console.log('🛒 Produit original:', productWithSlug);
                        const translatedProduct = translateProduct(productWithSlug, i18n.language);
                        console.log('🔄 Produit traduit:', translatedProduct);
                        
                        return (
                          <GlassCard 
                            key={`${product.orderId}-${product.productId}`}
                            className="overflow-hidden"
                          >
                            <div className="p-6">
                              <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
                                <div className="flex flex-wrap items-center gap-2 min-w-0">
                                  <h3 className="text-xl font-bold text-body hover:text-primary transition-colors duration-300">
                                    {translatedProduct.productName || translatedProduct.name}
                                  </h3>
                                  <Badge tone={product.requiresLicense ? 'cyan' : 'success'}>
                                    {product.requiresLicense
                                      ? t('profile.indicators.purchased.tagLicenseRequired')
                                      : t('profile.indicators.purchased.tagNoLicense')}
                                  </Badge>
                                </div>
                                <span className="text-primary font-semibold shrink-0">
                                  {formatDisplayPrice(product.price, i18n.language)}
                                </span>
                              </div>
                              
                              {translatedProduct.description && (
                                <p className="text-muted text-sm mb-4">
                                  {translatedProduct.description}
                                </p>
                              )}

                              <div className="flex flex-col space-y-3">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-muted">
                                    {t('profile.indicators.purchased.purchasedOn')} {new Date(product.purchaseDate).toLocaleDateString()}
                                  </span>
                                </div>
                                
                                <div className="flex space-x-2">
                                  {!product.requiresLicense ? (
                                    <button
                                      type="button"
                                      disabled
                                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-btn border border-white/10 bg-white/[.04] text-muted cursor-not-allowed"
                                      aria-disabled="true"
                                    >
                                      {t('profile.indicators.purchased.noActivationNeeded')}
                                    </button>
                                  ) : (
                                    <Button
                                      type="button"
                                      variant="secondary"
                                      onClick={() => handleLicenseRequest(product)}
                                      className="flex-1"
                                    >
                                      <KeyRound className="w-4 h-4" aria-hidden="true" />
                                      {t('profile.indicators.purchased.getLicense')}
                                    </Button>
                                  )}
                                  
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={async () => {
                                      try {
                                        console.log('🚀 Tentative de téléchargement pour:', product.productName);
                                        const success = await downloadProduct(product.productName, (error) => {
                                          console.error('❌ Erreur téléchargement:', error);
                                          setError(error);
                                        });
                                        if (success) {
                                          console.log('✅ Téléchargement réussi');
                                          setSuccessMessage('Téléchargement démarré avec succès');
                                        } else {
                                          console.log('❌ Téléchargement échoué');
                                          setError('Échec du téléchargement');
                                        }
                                      } catch (error) {
                                        console.error('❌ Erreur lors du téléchargement:', error);
                                        setError('Erreur lors du téléchargement');
                                      }
                                    }}
                                    className="flex-1"
                                  >
                                    <Download className="w-4 h-4" aria-hidden="true" />
                                    {t('profile.indicators.purchased.download')}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </GlassCard>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-muted">{t('profile.indicators.purchased.noIndicators')}</p>
                      <Button
                        to="/indicators"
                        className="mt-4"
                      >
                        {t('profile.indicators.purchased.discoverIndicators')}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'orders' && (
                <div className="space-y-6">
                    {orders.length === 0 ? (
                    <div className="text-center py-8">
                        <div className="text-muted">{t('profile.orders.noOrders')}</div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                          <tr>
                              <th scope="col" className="px-6 py-3 text-left text-muted text-xs uppercase tracking-wider">{t('profile.orders.columns.id')}</th>
                              <th scope="col" className="px-6 py-3 text-left text-muted text-xs uppercase tracking-wider">{t('profile.orders.columns.date')}</th>
                              <th scope="col" className="px-6 py-3 text-left text-muted text-xs uppercase tracking-wider">{t('profile.orders.columns.total')}</th>
                              <th scope="col" className="px-6 py-3 text-left text-muted text-xs uppercase tracking-wider">{t('profile.orders.columns.paid')}</th>
                              <th scope="col" className="px-6 py-3 text-left text-muted text-xs uppercase tracking-wider">{t('profile.orders.columns.items')}</th>
                              <th scope="col" className="px-6 py-3 text-left text-muted text-xs uppercase tracking-wider">{t('profile.orders.columns.invoice')}</th>
                          </tr>
                        </thead>
                          <tbody>
                          {orders.map((order) => (
                            <tr 
                              key={order._id} 
                                className="border-white/[.06] border-t hover:bg-white/[.02] transition-colors duration-150"
                            >
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-body/80">
                                {order._id.substring(0, 8)}...
                              </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-body/80">
                                {isValidDate(order.createdAt) ? new Date(order.createdAt).toLocaleDateString() : t('profile.orders.unknownDate')}
                              </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-body font-medium">
                                {formatDisplayPrice(order.totalPrice, i18n.language)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {order.isPaid ? (
                                    <span className="inline-flex items-center text-primary">
                                    {isValidDate(order.paidAt) ? new Date(order.paidAt).toLocaleDateString() : t('profile.orders.paid')}
                                  </span>
                                ) : (
                                    <span className="inline-flex items-center text-danger">
                                    {t('profile.orders.notPaid')}
                                  </span>
                                )}
                              </td>
                                <td className="px-6 py-4 text-sm text-body/80">
                                <div className="space-y-1">
                                  {order.orderItems && order.orderItems.map(item => (
                                    <div key={item.product || `item-${Math.random()}`}>
                                      {item.name} ({item.qty || item.quantity || 1})
                                    </div>
                                  ))}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                {order.isPaid ? (
                                  <div className="flex space-x-2">
                                    <Button
                                      variant="secondary"
                                      onClick={() => downloadInvoice(order._id)}
                                      disabled={downloadingInvoices.has(order._id)}
                                      className="!px-3 !py-1.5 text-sm"
                                    >
                                      {downloadingInvoices.has(order._id) ? (
                                        t('profile.orders.generating')
                                      ) : (
                                        <>
                                          <FileText className="w-4 h-4" />
                                          PDF
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                ) : (
                                    <span className="text-muted">N/A</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Modals */}
              <BillingForm 
                show={showBillingForm} 
                onHide={() => setShowBillingForm(false)} 
                onUpdate={(updatedUser) => {
                  setShowBillingForm(false);
                  // Mise à jour effectuée via localStorage par le formulaire
                }}
                user={userInfo}
              />
              
              <ProfileForm 
                show={showProfileForm} 
                onHide={() => setShowProfileForm(false)} 
                onUpdate={(updatedUser) => {
                  setShowProfileForm(false);
                  // Mise à jour effectuée via localStorage par le formulaire
                }}
                user={userInfo}
              />
              
              <LicenseModal 
                show={showLicenseModal} 
                onHide={() => setShowLicenseModal(false)} 
                product={selectedProduct}
                onSubmit={handleLicenseSubmit}
                loading={licenseLoading}
                existingRequests={licenseRequests}
              />
            </div>
          )}
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default ProfileScreen;
